/**
 * Which clicks may take an operator away from an aircraft they are flying.
 *
 * Manual authority ends when `useManualControl`'s `droneId` changes: the hook
 * abandons the session and hands the aircraft back. That is correct as a
 * mechanism and wrong as a user experience, because three ordinary clicks
 * reach it by accident — a background press on the map, a press on another
 * aircraft, and a press on a dock. An operator flying by hand lost the
 * aircraft to a stray click with no warning and no way back except requesting
 * authority again.
 *
 * So the selection dispatch is gated on this predicate while authority is
 * held, and the click becomes a proposal the operator confirms. Nothing here
 * blocks RTH, LAND or ABORT: those are command-bar verbs, not selection
 * changes, and they stay available in every state to every authorised operator
 * exactly as the feature's non-negotiables require.
 *
 * Pure, and deliberately reasoning about `SelectionAction` rather than about
 * DOM events — the reducer is the one place that decides what moves the
 * selection, so this stays honest by reading the same vocabulary.
 */
import type { SelectionAction } from './copReducers';

/**
 * Would this action move the selection off `flownDroneId`?
 *
 * Only `SELECT` and `SELECT_DOCK` write `selectedDroneId` in
 * `selectionReducer`; everything else (details, video pop-out, bay rows,
 * launch intent) leaves it alone and must not raise a dialog. A `SELECT` for
 * the aircraft already being flown is not leaving it — re-picking the same
 * aircraft is a no-op the operator should never be asked about.
 *
 * An empty `SELECT` id is the background map click (`DroneMap`'s
 * `handleMapClick` is its only caller). It counts as leaving: it clears the
 * selection, which ends the session just as surely as picking another
 * aircraft, and it is the easiest of the three to do by accident.
 */
export function leavesFlownAircraft(
  action: SelectionAction,
  flownDroneId: string | null,
): boolean {
  if (!flownDroneId) return false;
  switch (action.type) {
    case 'SELECT':
      return (action.id || null) !== flownDroneId;
    case 'SELECT_DOCK':
      // Always: the reducer nulls `selectedDroneId` for any dock pick, empty
      // id included.
      return true;
    default:
      return false;
  }
}

/** Heading and body for the confirmation, given the aircraft under the hand. */
export const MANUAL_EXIT_CONFIRM = {
  title: 'Leave manual flight?',
  body: (callsign: string) =>
    `You are flying ${callsign} by hand. Leaving hands the aircraft back to the RC and ends your manual session.`,
  /** The safe key, and the default: staying costs nothing. */
  stay: 'STAY ON AIRCRAFT',
  leave: 'RELEASE AND LEAVE',
} as const;
